<template>
  <div class="settings-page">
    <PageTitleBar
      title-id="settings-page-title"
      title="Impostazioni"
      subtitle="Gestisci le impostazioni, personalizza le configurazioni e organizza il tuo studio in un unico spazio."
      icon-src="/Titoli pagine/Icona Impostazioni.png"
      illustration-src="/Titoli pagine/Immagine Impostazioni.png"
      variant="settings"
    />
    <div class="settings-grid" :class="{ loading: isLoading }">
      <article class="settings-card settings-card--wide">
        <header class="settings-card-header">
          <div class="settings-icon-box settings-icon-box--appearance">
            <i class="fa-solid fa-palette"></i>
          </div>
          <div class="settings-card-heading">
            <h4>Aspetto</h4>
            <p>Personalizza l'aspetto dell'applicazione.</p>
          </div>
        </header>
        <div class="settings-card-divider"></div>
        <div class="settings-row">
          <div class="settings-row-copy">
            <span class="settings-row-label">Modalità Scura</span>
            <small
              >Attiva la modalità scura per un'esperienza più
              confortevole.</small
            >
          </div>
          <label
            class="theme-switch settings-theme-switch"
            title="Modalità Scura"
          >
            <input
              type="checkbox"
              v-model="isDarkMode"
              @change="toggleDarkMode"
            />
            <span class="slider-dark"></span>
            <i class="fa-solid fa-moon moon-icon"></i>
          </label>
        </div>
      </article>

      <article class="settings-card settings-card--wide">
        <header class="settings-card-header">
          <div class="settings-icon-box settings-icon-box--software">
            <i class="fa-solid fa-desktop"></i>
          </div>
          <div class="settings-card-heading">
            <h4>Software contabile predefinito</h4>
            <p>Imposta il software utilizzato dallo studio.</p>
          </div>
        </header>
        <div class="settings-card-divider"></div>
        <div class="settings-row settings-row--inline">
          <div class="settings-label-info">
            <span class="settings-row-label"
              >Software utilizzato dallo studio</span
            >
            <span class="info-icon-wrapper">
              <i class="fa-solid fa-circle-info info-icon-software"></i>
              <span class="tooltip-software">
                Selezionando qui un software contabile, nei convertitori e nelle
                librerie verrà preselezionato automaticamente.<br />
                È possibile cambiarlo in qualsiasi momento.
                <br /><br />
                Lascia "Default (chiedi ogni volta)" se preferisci impostarlo
                manualmente.
              </span>
            </span>
          </div>
          <select
            v-model="settings.software_contabile"
            @change="saveSettings"
            class="software-select settings-select"
          >
            <option value="default">Default (chiedi ogni volta)</option>
            <option value="TEAMSYSTEM">TEAMSYSTEM</option>
            <option value="DATEV">DATEV</option>
            <option value="ZUCCHETTI">ZUCCHETTI</option>
            <option value="SEAC">SEAC</option>
            <option value="SISTEMI">SISTEMI</option>
          </select>
        </div>
        <p class="settings-help-text">
          Seleziona il software contabile predefinito per le integrazioni e le
          funzionalità dello studio.
        </p>
      </article>

      <article class="settings-card settings-card--compact">
        <header class="settings-card-header">
          <div class="settings-icon-box settings-icon-box--paghe">
            <ProductIcon product="prospetti" />
          </div>
          <div class="settings-card-heading">
            <h4>Prospetti Paghe</h4>
            <p>Configura le opzioni di estrazione.</p>
          </div>
        </header>
        <div class="settings-card-divider"></div>
        <div class="settings-row settings-row--tight">
          <div class="settings-label-info settings-row-copy">
            <span class="settings-row-label"
              >Mostra il saldo DARE/AVERE per ogni voce contabile ripetuta</span
            >
            <span class="info-icon-wrapper">
              <i class="fa-solid fa-circle-info info-icon-software"></i>
              <span class="tooltip-software">
                Quando una voce contabile appare più volte nel Prospetto
                Paghe,<br />
                viene calcolato e mostrato il saldo totale DARE/AVERE.<br /><br />
                Disattivando l'opzione verrà mostrata la somma in positivo del
                DARE e la somma in positivo dell'AVERE.
              </span>
            </span>
          </div>
          <label class="toggle-switch">
            <input
              type="checkbox"
              v-model="settings.flag_saldoProspettiPaghe"
              @change="saveSettings"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="settings-field-block">
          <div class="settings-label-info">
            <span class="settings-row-label"
              >Preferenze di estrazione per:</span
            >
            <span class="info-icon-wrapper section-software-info">
              <i class="fa-solid fa-circle-info info-icon-software"></i>
              <span class="tooltip-software section-software-tooltip">
                Configura le sezioni da leggere nei Prospetti Paghe quando usi
                il Convertitore.
              </span>
            </span>
          </div>
          <select
            v-model="sectionPreferences.software"
            class="software-select settings-select settings-select--full"
            @change="handleSectionPreferencesSoftwareChange"
          >
            <option value="">Seleziona software</option>
            <option
              v-for="software in sectionPreferences.availableSoftware"
              :key="software"
              :value="software"
            >
              {{ software }}
            </option>
          </select>
          <button
            v-if="sectionPreferences.software"
            type="button"
            class="section-preferences-open-button"
            :disabled="sectionPreferences.loading"
            @click="openSectionPreferencesModal"
          >
            <i class="fa-solid fa-sliders"></i>
            <span>
              {{
                sectionPreferences.loading
                  ? "Caricamento configurazione..."
                  : "Apri configurazione sezioni"
              }}
            </span>
          </button>
        </div>
        <p class="settings-help-text">
          Seleziona un software contabile per gestire le preferenze delle
          sezioni da estrarre.
        </p>
      </article>

      <article class="settings-card settings-card--compact">
        <header class="settings-card-header">
          <div class="settings-icon-box settings-icon-box--f24">
            <ProductIcon product="f24" />
          </div>
          <div class="settings-card-heading">
            <h4>Modelli F24</h4>
            <p>Gestisci le opzioni di elaborazione.</p>
          </div>
        </header>
        <div class="settings-card-divider"></div>
        <div class="settings-row settings-row--tight">
          <div class="settings-label-info settings-row-copy">
            <span class="settings-row-label"
              >Mostra il saldo DARE/AVERE per ogni voce contabile ripetuta</span
            >
            <span class="info-icon-wrapper">
              <i class="fa-solid fa-circle-info info-icon-software"></i>
              <span class="tooltip-software">
                Quando una voce contabile appare più volte nel Modello F24,<br />
                viene calcolato e mostrato il saldo totale DARE/AVERE.
              </span>
            </span>
          </div>
          <label class="toggle-switch">
            <input
              type="checkbox"
              v-model="settings.flag_saldoF24"
              @change="saveSettings"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div
          class="settings-row settings-row--tight settings-row--f24-aggregate"
        >
          <span class="settings-row-label"
            >Elabora F24 accorpato per codici conto</span
          >
          <label class="toggle-switch">
            <input
              type="checkbox"
              v-model="settings.flag_accorpato"
              @change="saveSettings"
            />
            <span class="slider"></span>
          </label>
        </div>
        <p class="settings-help-text settings-help-text--f24-aggregate">
          Attiva questa opzione per elaborare un singolo modello F24 con codice
          conto accorpato.
        </p>
      </article>

      <article class="settings-card settings-card--compact">
        <header class="settings-card-header">
          <div class="settings-icon-box settings-icon-box--bilancio">
            <ProductIcon product="bilancio" />
          </div>
          <div class="settings-card-heading">
            <h4>Bilancio Analitico</h4>
            <p>Attiva i dettagli per clienti e fornitori.</p>
          </div>
        </header>
        <div class="settings-card-divider"></div>
        <div class="settings-row settings-row--tight">
          <div class="settings-row-copy">
            <span class="settings-row-label"
              >Attiva sempre Dettaglio Clienti/Fornitori</span
            >
            <small>
              Mostra il dettaglio di clienti e fornitori nei report di bilancio
              analitico.
            </small>
          </div>
          <label class="toggle-switch">
            <input
              type="checkbox"
              v-model="settings.flag_dettClientiFornitori"
              @change="saveSettings"
            />
            <span class="slider"></span>
          </label>
        </div>
        <div class="settings-row settings-row--tight">
          <span class="settings-row-label"
            >Attiva Dettaglio Clienti/Fornitori con Codice Fiscale</span
          >
          <label class="toggle-switch">
            <input
              type="checkbox"
              v-model="settings.flag_CFPIVA"
              @change="saveSettings"
            />
            <span class="slider"></span>
          </label>
        </div>
      </article>

      <article class="settings-card settings-card--full">
        <header class="settings-card-header">
          <div class="settings-icon-box">
            <i class="fa-solid fa-right-left"></i>
          </div>
          <div class="settings-card-heading">
            <h4>Tutti i Convertitori</h4>
            <p>Impostazioni generali dei convertitori.</p>
          </div>
        </header>
        <div class="settings-card-divider"></div>
        <div class="settings-row settings-row--full">
          <div class="settings-row-main">
            <span class="settings-row-label"
              >Attiva estrazione Codice Ditta in anagrafica clienti</span
            >
            <label class="toggle-switch">
              <input
                type="checkbox"
                v-model="settings.flag_mostra_codice_ditta"
                @change="saveSettings"
              />
              <span class="slider"></span>
            </label>
          </div>
          <span class="settings-row-note">
            Estrae il codice ditta nelle anagrafiche clienti per tutti i
            convertitori abilitati.
          </span>
        </div>
      </article>
    </div>

    <div
      v-if="sectionPreferencesModalOpen"
      class="section-preferences-modal-backdrop"
      @click.self="closeSectionPreferencesModal"
    >
      <section
        class="section-preferences-modal"
        role="dialog"
        aria-modal="true"
      >
        <header class="section-preferences-modal-header">
          <div>
            <span class="section-preferences-modal-eyebrow">
              Preferenze di estrazione
            </span>
            <h4>Configurazione Prospetti Paghe</h4>
            <p>
              Software selezionato:
              <strong>{{ sectionPreferences.software }}</strong>
            </p>
          </div>
          <button
            type="button"
            class="section-preferences-modal-close"
            aria-label="Chiudi configurazione preferenze"
            @click="closeSectionPreferencesModal"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div
          v-if="sectionPreferences.loading"
          class="section-preferences-empty"
        >
          Caricamento preferenze...
        </div>
        <div
          v-else-if="!sectionPreferences.catalog.length"
          class="section-preferences-empty"
        >
          Le sezioni personalizzabili per questo software non sono ancora
          disponibili.
        </div>
        <div v-else class="section-preferences-panel">
          <div class="section-preferences-actions">
            <button
              type="button"
              class="section-preferences-disclosure"
              :class="{ active: sectionPreferences.globalOpen }"
              @click="toggleGlobalSectionPreferences"
            >
              <i class="fa-solid fa-globe"></i>
              <span class="section-preferences-disclosure-label">
                Configurazione generale
              </span>
              <i
                :class="
                  sectionPreferences.globalOpen
                    ? 'fa-solid fa-chevron-up'
                    : 'fa-solid fa-chevron-down'
                "
              ></i>
            </button>
            <button
              type="button"
              class="section-preferences-disclosure"
              :class="{ active: sectionPreferences.clientsOpen }"
              @click="toggleClientSectionPreferences"
            >
              <i class="fa-solid fa-users-gear"></i>
              <span>Personalizzata per cliente</span>
              <i
                :class="
                  sectionPreferences.clientsOpen
                    ? 'fa-solid fa-chevron-up'
                    : 'fa-solid fa-chevron-down'
                "
              ></i>
            </button>
          </div>

          <section
            v-if="sectionPreferences.globalOpen"
            class="section-preferences-foldout"
          >
            <div class="section-preferences-header">
              <div class="section-preferences-header-copy">
                <strong class="section-preferences-header-title">
                  Configurazione estrazione
                </strong>
                <span class="section-preferences-header-description">
                  Sezioni da cui leggere i dati nei Prospetti Paghe.
                  <span class="settings-info-icon-wrapper">
                    <i class="fa-solid fa-circle-info"></i>
                    <span class="settings-info-tooltip section-catalog-tooltip">
                      Qui vengono mostrate le sezioni individuate nei prospetti
                      paghe elaborati. Puoi personalizzare la configurazione, se
                      lo desideri.
                      <template v-if="!sectionPreferences.canManage">
                        Contatta l'amministratore del tuo studio per modificare
                        le preferenze del convertitore.
                      </template>
                    </span>
                  </span>
                </span>
              </div>
              <div class="section-preferences-header-actions">
                <button
                  v-if="globalSectionPreferenceModified"
                  type="button"
                  class="section-preferences-restore"
                  :disabled="
                    sectionPreferences.restoring ||
                    !sectionPreferences.canManage
                  "
                  @click="requestRestoreGlobalSectionPreference"
                >
                  <i class="fa-solid fa-rotate-left"></i>
                  <span>Ripristina MyStudio</span>
                </button>
                <button
                  type="button"
                  class="section-preferences-reset"
                  :disabled="
                    !sectionPreferences.canManage ||
                    !globalSectionPreferencesDirty
                  "
                  @click="resetGlobalSectionPreferenceChanges"
                >
                  <i class="fa-solid fa-rotate-left"></i>
                  <span>Annulla</span>
                </button>
                <button
                  type="button"
                  class="section-preferences-save"
                  :disabled="
                    !sectionPreferences.canManage ||
                    sectionPreferences.saving ||
                    !globalSectionPreferencesDirty
                  "
                  @click="saveGlobalSectionPreference"
                >
                  <i class="fa-solid fa-floppy-disk"></i>
                  <span>
                    {{ sectionPreferences.saving ? "Salvataggio..." : "Salva" }}
                  </span>
                </button>
                <span
                  v-if="!sectionPreferences.canManage"
                  class="section-preferences-readonly-badge"
                  :title="sectionPreferencesPermissionTooltip"
                >
                  SOLA VISUALIZZAZIONE
                </span>
              </div>
            </div>
            <div class="section-preferences-list">
              <label
                v-for="section in sectionPreferences.catalog"
                :key="section.section_name"
                class="section-preference-row"
                :class="{
                  changed: isGlobalSectionPreferenceChanged(
                    section.section_name
                  ),
                  'changed-included':
                    isGlobalSectionPreferenceChanged(section.section_name) &&
                    sectionPreferences.sectionStates[section.section_name],
                  'changed-excluded':
                    isGlobalSectionPreferenceChanged(section.section_name) &&
                    !sectionPreferences.sectionStates[section.section_name],
                }"
              >
                <input
                  type="checkbox"
                  :disabled="!sectionPreferences.canManage"
                  v-model="
                    sectionPreferences.sectionStates[section.section_name]
                  "
                />
                <span class="section-preference-name">
                  {{ section.section_name }}
                </span>
              </label>
            </div>
          </section>

          <section
            v-if="sectionPreferences.clientsOpen"
            class="section-preferences-foldout"
          >
            <div class="client-section-preferences">
              <strong>Configurazioni personalizzate per cliente</strong>
              <div
                v-if="!clientSectionPreferences.length"
                class="section-preferences-empty"
              >
                Non ci sono configurazioni cliente per questo software.
              </div>
              <div v-else class="client-section-preferences-list">
                <div
                  v-for="preference in clientSectionPreferences"
                  :key="preference.id"
                  class="client-section-preference-row"
                  :class="{
                    active:
                      sectionPreferences.selectedClientPreferenceId ===
                      preference.id,
                  }"
                >
                  <button
                    type="button"
                    class="client-section-preference-select"
                    @click="selectClientSectionPreference(preference)"
                  >
                    <span>{{ preference.client_denominazione }}</span>
                    <small>{{ preference.client_codice_fiscale }}</small>
                  </button>
                  <button
                    type="button"
                    class="client-section-preference-delete"
                    :disabled="
                      !sectionPreferences.canManage ||
                      sectionPreferences.deletingClientId === preference.id
                    "
                    @click="requestDeleteClientSectionPreference(preference)"
                  >
                    <i class="fa-solid fa-trash"></i>
                  </button>
                </div>
              </div>

              <div
                v-if="selectedClientSectionPreference"
                class="client-section-preference-editor"
              >
                <div class="client-section-editor-header">
                  <div class="client-section-editor-copy">
                    <strong>Configurazione cliente</strong>
                    <span class="client-section-editor-name">
                      {{ selectedClientSectionPreference.client_denominazione }}
                    </span>
                    <span>
                      Le sezioni selezionate qui sovrascrivono la configurazione
                      generale per questo cliente.
                    </span>
                  </div>
                  <div class="client-section-editor-actions">
                    <button
                      type="button"
                      class="section-preferences-reset"
                      :disabled="
                        !sectionPreferences.canManage ||
                        !clientSectionPreferencesDirty
                      "
                      @click="resetClientSectionPreferenceChanges"
                    >
                      <i class="fa-solid fa-rotate-left"></i>
                      <span>Annulla</span>
                    </button>
                    <button
                      type="button"
                      class="section-preferences-save"
                      :disabled="
                        !sectionPreferences.canManage ||
                        sectionPreferences.clientSaving ||
                        !clientSectionPreferencesDirty
                      "
                      @click="saveClientSectionPreference"
                    >
                      <i class="fa-solid fa-floppy-disk"></i>
                      <span>
                        {{
                          sectionPreferences.clientSaving
                            ? "Salvataggio..."
                            : "Salva"
                        }}
                      </span>
                    </button>
                  </div>
                </div>
                <div class="section-preferences-list">
                  <label
                    v-for="section in sectionPreferences.catalog"
                    :key="section.section_name"
                    class="section-preference-row"
                    :class="{
                      'permanent-diff': isClientSectionPreferencePermanentDiff(
                        section.section_name
                      ),
                      changed: isClientSectionPreferenceChanged(
                        section.section_name
                      ),
                      'changed-included':
                        isClientSectionPreferenceChanged(
                          section.section_name
                        ) &&
                        sectionPreferences.clientSectionStates[
                          section.section_name
                        ],
                      'changed-excluded':
                        isClientSectionPreferenceChanged(
                          section.section_name
                        ) &&
                        !sectionPreferences.clientSectionStates[
                          section.section_name
                        ],
                    }"
                  >
                    <input
                      type="checkbox"
                      :disabled="!sectionPreferences.canManage"
                      v-model="
                        sectionPreferences.clientSectionStates[
                          section.section_name
                        ]
                      "
                    />
                    <span class="section-preference-name">
                      {{ section.section_name }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>

    <ConfirmActionModal
      :visible="confirmActionModal.visible"
      :title="confirmActionModal.title"
      :eyebrow="confirmActionModal.eyebrow"
      :message="confirmActionModal.message"
      :confirm-label="confirmActionModal.confirmLabel"
      :cancel-label="confirmActionModal.cancelLabel"
      :icon-class="confirmActionModal.iconClass"
      :confirm-icon-class="confirmActionModal.confirmIconClass"
      :tone="confirmActionModal.tone"
      @close="closeConfirmActionModal"
      @confirm="confirmPendingAction"
    />
  </div>
</template>
<script>
import { theme } from "@/store/theme.js";
import axios from "axios";
import { API_BASE_URL } from "@/config.js";
import ConfirmActionModal from "@/components/ConfirmActionModal.vue";
import PageTitleBar from "@/components/PageTitleBar.vue";
import ProductIcon from "@/components/ProductIcon.vue";

export default {
  name: "ImpostazioniApp",
  components: {
    ConfirmActionModal,
    PageTitleBar,
    ProductIcon,
  },
  data() {
    return {
      isDarkMode: false,
      isLoading: false, // SKELETON ATTIVO
      settings: {
        flag_saldoProspettiPaghe: true,
        flag_saldoF24: true,
        flag_accorpato: false,
        flag_mostra_codice_ditta: true,
      },
      sectionPreferences: {
        loading: false,
        saving: false,
        canManage: false,
        software: "",
        availableSoftware: [],
        globalOpen: false,
        clientsOpen: false,
        catalog: [],
        preferences: [],
        sectionStates: {},
        originalSectionStates: {},
        selectedClientPreferenceId: null,
        draftClientPreference: null,
        clientSectionStates: {},
        originalClientSectionStates: {},
        clientSaving: false,
        restoring: false,
        deletingClientId: null,
      },
      sectionPreferencesModalOpen: false,
      confirmActionModal: {
        visible: false,
        title: "",
        eyebrow: "",
        message: "",
        confirmLabel: "Conferma",
        cancelLabel: "Annulla",
        iconClass: "fa-solid fa-circle-info",
        confirmIconClass: "fa-solid fa-check",
        tone: "info",
        action: null,
        payload: null,
      },
    };
  },
  computed: {
    sectionPreferencesPermissionTooltip() {
      return this.sectionPreferences.canManage
        ? ""
        : "Contatta l'amministratore del tuo studio per modificare le preferenze del convertitore";
    },
    globalSectionPreference() {
      return (
        this.sectionPreferences.preferences.find(
          (preference) => preference.scope_type === "global"
        ) || null
      );
    },
    clientSectionPreferences() {
      return this.sectionPreferences.preferences
        .filter((preference) => preference.scope_type === "client")
        .sort((a, b) =>
          String(a.client_denominazione || "").localeCompare(
            String(b.client_denominazione || ""),
            "it",
            { sensitivity: "base" }
          )
        );
    },
    globalSectionPreferenceModified() {
      if (!this.globalSectionPreference) return false;
      const sectionMap = this.globalSectionPreference.section_overrides || {};
      const defaults = Object.fromEntries(
        (this.sectionPreferences.catalog || []).map((section) => [
          section.section_name,
          Boolean(section.default_include),
        ])
      );
      return Object.entries(sectionMap).some(([sectionName, include]) => {
        return Boolean(include) !== Boolean(defaults[sectionName]);
      });
    },
    mystudioDefaultIncludedSections() {
      return (this.sectionPreferences.catalog || [])
        .filter((section) => Boolean(section.default_include))
        .map((section) => section.section_name)
        .sort((a, b) =>
          String(a || "").localeCompare(String(b || ""), "it", {
            sensitivity: "base",
          })
        );
    },
    selectedClientSectionPreference() {
      return (
        this.clientSectionPreferences.find(
          (preference) =>
            preference.id === this.sectionPreferences.selectedClientPreferenceId
        ) ||
        this.sectionPreferences.draftClientPreference ||
        null
      );
    },
    globalSectionPreferencesDirty() {
      const current = this.sectionPreferences.sectionStates || {};
      const original = this.sectionPreferences.originalSectionStates || {};
      const keys = new Set([...Object.keys(current), ...Object.keys(original)]);
      if (!keys.size) return false;

      for (const key of keys) {
        if (Boolean(current[key]) !== Boolean(original[key])) {
          return true;
        }
      }
      return false;
    },
    clientSectionPreferencesDirty() {
      const current = this.sectionPreferences.clientSectionStates || {};
      const original =
        this.sectionPreferences.originalClientSectionStates || {};
      const keys = new Set([...Object.keys(current), ...Object.keys(original)]);
      if (!keys.size) return false;

      for (const key of keys) {
        if (Boolean(current[key]) !== Boolean(original[key])) {
          return true;
        }
      }
      return false;
    },
    isDraftClientSectionPreference() {
      return String(this.selectedClientSectionPreference?.id || "").startsWith(
        "new-"
      );
    },
  },
  methods: {
    toggleDarkMode() {
      theme.toggle();
      this.isDarkMode = theme.darkMode;
    },

    async loadSettings() {
      this.isLoading = true;
      try {
        const res = await axios.get(`${API_BASE_URL}/settings`);
        this.settings = res.data;
        this.syncSectionPreferenceSoftwareWithDefault();
        // Aggiorna cache
        localStorage.setItem("userSettings", JSON.stringify(this.settings));
      } catch (err) {
        console.warn("Impostazioni non caricate, uso default", err);
        // Fallback a default
      } finally {
        this.isLoading = false;
      }
    },

    async saveSettings() {
      try {
        await axios.post(`${API_BASE_URL}/settings`, this.settings);
        // Aggiorna cache
        localStorage.setItem("userSettings", JSON.stringify(this.settings));
        const sectionSoftwareChanged =
          this.syncSectionPreferenceSoftwareWithDefault();
        if (sectionSoftwareChanged) {
          await this.loadSectionPreferences();
        }
        console.log("Impostazioni salvate + cache aggiornata");
      } catch (err) {
        console.error("Errore salvataggio", err);
        alert("Errore nel salvataggio. Riprova.");
      }
    },
    syncSectionPreferenceSoftwareWithDefault() {
      const preferredSoftware = String(
        this.settings.software_contabile || ""
      ).toUpperCase();
      const available = this.sectionPreferences.availableSoftware || [];
      const nextSoftware = available.includes(preferredSoftware)
        ? preferredSoftware
        : "";

      if (this.sectionPreferences.software === nextSoftware) {
        return false;
      }

      this.sectionPreferences.software = nextSoftware;
      return true;
    },
    async handleSectionPreferencesSoftwareChange() {
      await this.loadSectionPreferences();
    },
    openSectionPreferencesModal() {
      if (!this.sectionPreferences.software) return;
      this.sectionPreferencesModalOpen = true;
      if (
        !this.sectionPreferences.globalOpen &&
        !this.sectionPreferences.clientsOpen
      ) {
        this.sectionPreferences.globalOpen = true;
      }
    },
    closeSectionPreferencesModal() {
      this.sectionPreferencesModalOpen = false;
    },
    toggleGlobalSectionPreferences() {
      const shouldOpen = !this.sectionPreferences.globalOpen;
      this.sectionPreferences.globalOpen = shouldOpen;
      if (shouldOpen) {
        this.sectionPreferences.clientsOpen = false;
      }
    },
    toggleClientSectionPreferences() {
      const shouldOpen = !this.sectionPreferences.clientsOpen;
      this.sectionPreferences.clientsOpen = shouldOpen;
      if (shouldOpen) {
        this.sectionPreferences.globalOpen = false;
      }
    },
    applySectionPreferenceState() {
      const globalOverrides =
        this.globalSectionPreference?.section_overrides || {};
      this.sectionPreferences.sectionStates = Object.fromEntries(
        this.sectionPreferences.catalog.map((section) => [
          section.section_name,
          Object.prototype.hasOwnProperty.call(
            globalOverrides,
            section.section_name
          )
            ? Boolean(globalOverrides[section.section_name])
            : Boolean(section.default_include),
        ])
      );
      this.sectionPreferences.originalSectionStates = {
        ...this.sectionPreferences.sectionStates,
      };
    },
    resetSelectedClientPreference() {
      this.sectionPreferences.selectedClientPreferenceId = null;
      this.sectionPreferences.draftClientPreference = null;
      this.sectionPreferences.clientSectionStates = {};
      this.sectionPreferences.originalClientSectionStates = {};
    },
    isGlobalSectionPreferenceChanged(sectionName) {
      return (
        Boolean(this.sectionPreferences.sectionStates[sectionName]) !==
        Boolean(this.sectionPreferences.originalSectionStates[sectionName])
      );
    },
    isClientSectionPreferenceChanged(sectionName) {
      return (
        Boolean(this.sectionPreferences.clientSectionStates[sectionName]) !==
        Boolean(
          this.sectionPreferences.originalClientSectionStates[sectionName]
        )
      );
    },
    isClientSectionPreferencePermanentDiff(sectionName) {
      if (
        !this.selectedClientSectionPreference ||
        this.isDraftClientSectionPreference
      ) {
        return false;
      }
      return (
        Boolean(
          this.sectionPreferences.originalClientSectionStates[sectionName]
        ) !==
        Boolean(this.sectionPreferences.originalSectionStates[sectionName])
      );
    },
    resetGlobalSectionPreferenceChanges() {
      if (!this.globalSectionPreferencesDirty) return;
      this.sectionPreferences.sectionStates = {
        ...this.sectionPreferences.originalSectionStates,
      };
    },
    resetClientSectionPreferenceChanges() {
      if (!this.clientSectionPreferencesDirty) return;
      this.sectionPreferences.clientSectionStates = {
        ...this.sectionPreferences.originalClientSectionStates,
      };
    },
    sectionMapFromPreference(preference) {
      const sectionOverrides = preference?.section_overrides || {};
      return Object.fromEntries(
        this.sectionPreferences.catalog.map((section) => [
          section.section_name,
          Object.prototype.hasOwnProperty.call(
            sectionOverrides,
            section.section_name
          )
            ? Boolean(sectionOverrides[section.section_name])
            : Boolean(
                this.sectionPreferences.sectionStates[section.section_name]
              ),
        ])
      );
    },
    selectClientSectionPreference(preference) {
      if (
        this.sectionPreferences.selectedClientPreferenceId === preference.id
      ) {
        this.resetSelectedClientPreference();
        return;
      }

      this.sectionPreferences.draftClientPreference = null;
      this.sectionPreferences.selectedClientPreferenceId = preference.id;
      const sectionStates = this.sectionMapFromPreference(preference);
      this.sectionPreferences.clientSectionStates = sectionStates;
      this.sectionPreferences.originalClientSectionStates = {
        ...sectionStates,
      };
    },
    async loadSectionPreferences() {
      this.sectionPreferences.loading = true;
      try {
        const params = {};
        if (this.sectionPreferences.software) {
          params.software_contabile = this.sectionPreferences.software;
        }
        const response = await axios.get(
          `${API_BASE_URL}/settings/prospetti_paghe/section_preferences`,
          { params }
        );
        this.sectionPreferences.availableSoftware =
          response.data.available_software || [];
        if (
          this.sectionPreferences.software &&
          !this.sectionPreferences.availableSoftware.includes(
            this.sectionPreferences.software
          )
        ) {
          this.sectionPreferences.software = "";
        }
        this.sectionPreferences.catalog = response.data.catalog || [];
        this.sectionPreferences.preferences = response.data.preferences || [];
        this.sectionPreferences.canManage = Boolean(
          response.data.can_manage_section_preferences
        );
        this.sectionPreferences.globalOpen = false;
        this.sectionPreferences.clientsOpen = false;
        this.resetSelectedClientPreference();
        this.applySectionPreferenceState();
      } catch (err) {
        console.error("Preferenze sezioni non caricate", err);
        this.sectionPreferences.catalog = [];
        this.sectionPreferences.preferences = [];
        this.sectionPreferences.canManage = false;
        this.sectionPreferences.sectionStates = {};
        this.sectionPreferences.originalSectionStates = {};
        this.resetSelectedClientPreference();
      } finally {
        this.sectionPreferences.loading = false;
      }
    },
    async saveGlobalSectionPreference() {
      if (
        !this.sectionPreferences.canManage ||
        this.sectionPreferences.saving ||
        !this.globalSectionPreferencesDirty
      ) {
        return;
      }

      this.sectionPreferences.saving = true;
      try {
        await axios.post(
          `${API_BASE_URL}/settings/prospetti_paghe/section_preferences`,
          {
            software_contabile: this.sectionPreferences.software,
            scope_type: "global",
            section_overrides: this.sectionPreferences.sectionStates,
          }
        );
        await this.loadSectionPreferences();
      } catch (err) {
        console.error("Errore salvataggio preferenze sezioni", err);
        alert("Errore nel salvataggio delle preferenze sezioni.");
      } finally {
        this.sectionPreferences.saving = false;
      }
    },
    requestRestoreGlobalSectionPreference() {
      this.confirmActionModal = {
        visible: true,
        title: "Ripristina configurazione",
        eyebrow: "Impostazioni di estrazione",
        message:
          "La configurazione globale tornerà alla preferenza di sezioni fornita da MyStudio.",
        confirmLabel: "Ripristina",
        cancelLabel: "Annulla",
        iconClass: "fa-solid fa-rotate-left",
        confirmIconClass: "fa-solid fa-rotate-left",
        tone: "info",
        action: "restoreGlobal",
        payload: null,
      };
    },
    async restoreGlobalSectionPreference() {
      if (
        !this.sectionPreferences.canManage ||
        this.sectionPreferences.restoring ||
        !this.globalSectionPreferenceModified
      ) {
        return;
      }

      this.sectionPreferences.restoring = true;
      try {
        await axios.delete(
          `${API_BASE_URL}/settings/prospetti_paghe/section_preferences`,
          {
            data: {
              software_contabile: this.sectionPreferences.software,
              scope_type: "global",
            },
          }
        );
        await this.loadSectionPreferences();
        this.sectionPreferences.globalOpen = true;
        this.sectionPreferences.clientsOpen = false;
      } catch (err) {
        console.error("Errore ripristino preferenze sezioni", err);
        alert("Errore nel ripristino delle preferenze sezioni.");
      } finally {
        this.sectionPreferences.restoring = false;
      }
    },
    async saveClientSectionPreference() {
      const preference = this.selectedClientSectionPreference;
      if (
        !preference ||
        !this.sectionPreferences.canManage ||
        this.sectionPreferences.clientSaving ||
        !this.clientSectionPreferencesDirty
      ) {
        return;
      }

      this.sectionPreferences.clientSaving = true;
      try {
        await axios.post(
          `${API_BASE_URL}/settings/prospetti_paghe/section_preferences`,
          {
            software_contabile: this.sectionPreferences.software,
            scope_type: "client",
            client_denominazione: preference.client_denominazione,
            client_codice_fiscale: preference.client_codice_fiscale,
            section_overrides: this.sectionPreferences.clientSectionStates,
          }
        );
        await this.loadSectionPreferences();
        const refreshedPreference = this.clientSectionPreferences.find(
          (item) =>
            item.client_key === preference.client_key ||
            item.id === preference.id ||
            (item.client_denominazione === preference.client_denominazione &&
              item.client_codice_fiscale === preference.client_codice_fiscale)
        );
        if (refreshedPreference) {
          this.sectionPreferences.clientsOpen = true;
          this.sectionPreferences.globalOpen = false;
          this.selectClientSectionPreference(refreshedPreference);
        }
      } catch (err) {
        console.error("Errore salvataggio preferenze cliente", err);
        alert("Errore nel salvataggio delle preferenze del cliente.");
      } finally {
        this.sectionPreferences.clientSaving = false;
      }
    },
    requestDeleteClientSectionPreference(preference) {
      this.confirmActionModal = {
        visible: true,
        title: "Elimina configurazione cliente",
        eyebrow: preference?.client_denominazione || "Cliente",
        message:
          "La configurazione personalizzata verra eliminata e il cliente userà la configurazione globale.",
        confirmLabel: "Elimina",
        cancelLabel: "Annulla",
        iconClass: "fa-solid fa-trash-can",
        confirmIconClass: "fa-solid fa-trash-can",
        tone: "danger",
        action: "deleteClient",
        payload: preference,
      };
    },
    closeConfirmActionModal() {
      this.confirmActionModal.visible = false;
      this.confirmActionModal.action = null;
      this.confirmActionModal.payload = null;
    },
    async confirmPendingAction() {
      const { action, payload } = this.confirmActionModal;
      this.closeConfirmActionModal();

      if (action === "restoreGlobal") {
        await this.restoreGlobalSectionPreference();
        return;
      }
      if (action === "deleteClient") {
        await this.deleteClientSectionPreference(payload);
      }
    },
    async deleteClientSectionPreference(preference) {
      if (
        !preference ||
        !this.sectionPreferences.canManage ||
        this.sectionPreferences.deletingClientId === preference.id
      ) {
        return;
      }

      this.sectionPreferences.deletingClientId = preference.id;
      try {
        await axios.delete(
          `${API_BASE_URL}/settings/prospetti_paghe/section_preferences`,
          {
            data: {
              software_contabile: this.sectionPreferences.software,
              scope_type: "client",
              client_denominazione: preference.client_denominazione,
              client_codice_fiscale: preference.client_codice_fiscale,
            },
          }
        );
        await this.loadSectionPreferences();
        this.sectionPreferences.clientsOpen = true;
        this.sectionPreferences.globalOpen = false;
      } catch (err) {
        console.error("Errore eliminazione preferenze cliente", err);
        alert("Errore nell'eliminazione della configurazione cliente.");
      } finally {
        this.sectionPreferences.deletingClientId = null;
      }
    },
  },
  async mounted() {
    theme.apply();
    this.isDarkMode = theme.darkMode;

    // Usa cache se esiste
    const cached = localStorage.getItem("userSettings");
    if (cached) {
      try {
        this.settings = JSON.parse(cached);
        this.syncSectionPreferenceSoftwareWithDefault();
        this.isLoading = false;
        console.log("Impostazioni caricate da cache");
      } catch (e) {
        await this.loadSettings();
      }
    } else {
      await this.loadSettings(); // Solo se non c'e cache
    }
    await this.loadSectionPreferences();
    if (this.syncSectionPreferenceSoftwareWithDefault()) {
      await this.loadSectionPreferences();
    }
  },
};
</script>

<style scoped>
/* === STILI LOCALI === */
.impostazioni {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.8rem 0;
  font-size: 0.95rem;
}

.impostazioni span {
  font-weight: 500;
  padding-left: 0.75rem;
}

/* === SKELETON (stile Coin Card) === */
.skeleton-loader {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.skeleton {
  background: #f0f0f0;
  border-radius: 4px;
  animation: pulse 1.5s infinite ease-in-out;
}

.skeleton-text {
  height: 1rem;
  width: 70%;
}

.skeleton-toggle {
  height: 26px;
  width: 50px;
  border-radius: 34px;
}

/* Dark Mode per skeleton */
.dm .skeleton {
  background: #444;
}
.dm .skeleton::after {
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.1),
    transparent
  );
}

/* Animazione pulse (identica a Coin Card) */
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* === TOGGLE SWITCH === */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #4caf50;
}

input:checked + .slider:before {
  transform: translateX(24px);
}

/* Theme Switch (Dark Mode) */
.theme-switch {
  position: relative;
  display: inline-block;
  width: 62px;
  height: 27px;
  cursor: pointer;
}

.theme-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Sezioni */
.sezione {
  min-width: 33rem;
  margin: 2rem 0;
  padding: 1rem 2rem;
  border-radius: 12px;
  background: var(--card-bg, #fff);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.converter-preferences-section {
  width: min(44rem, calc(100vw - 2rem));
  max-width: 44rem;
  box-sizing: border-box;
}

.sezione h4 {
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  color: var(--text-primary, #333);
  font-weight: 600;
}
.sezione span {
  text-align: left;
}

/* Dark Mode */
.dm .sezione {
  background: #2d2d2d;
}
.dm .impostazioni span {
  color: #eee;
}
.dm .slider {
  background-color: #555;
}
.dm input:checked + .slider {
  background-color: #66bb6a;
}
.section-title {
  text-align: left;
  font-size: 1.05rem !important;
  margin-top: 1.75rem !important;
  color: #6b6b6b !important;
}
.software-select {
  padding: 0.5rem 0.8rem;
  border-radius: 8px;
  border: 1px solid #ccc;
  background: white;
  font-size: 0.95rem;
  min-width: 180px;
}
.section-preferences-feature-row {
  justify-content: flex-start;
  gap: 0.35rem;
  margin-top: 1rem;
}
.section-preferences-feature-row .section-preferences-inline-select {
  min-width: 120px;
  max-width: 175px;
  padding: 0.35rem 1.85rem 0.35rem 0.55rem;
  border-color: rgba(72, 84, 116, 0.18);
  color: #0b2b48;
  font-size: 0.95rem;
  font-weight: 500;
}
.section-preferences-feature-row span {
  color: #0b2b48;
}
.section-preferences-feature-row .section-preferences-software-separator {
  padding-left: 0;
  color: #607178;
}
.section-preferences-feature-row .section-software-info {
  flex: 0 0 auto;
  padding-left: 0;
  margin-left: 0.15rem;
}
.section-preferences-feature-row .section-software-info .info-icon-software {
  color: #2d7eb3;
  font-size: 0.82rem;
}
.section-software-tooltip {
  width: 280px;
  max-width: min(280px, calc(100vw - 2rem));
  background: #102a43;
  color: #ffffff !important;
  white-space: normal;
  text-align: left;
  z-index: 10000;
}
.section-software-tooltip,
.section-software-tooltip * {
  color: #ffffff !important;
}
.section-preferences-empty {
  width: calc(100% - 0.75rem);
  margin-left: 0.75rem;
  padding: 1rem;
  box-sizing: border-box;
  border: 1px dashed rgba(30, 94, 80, 0.22);
  border-radius: 8px;
  color: #607178;
  font-size: 0.92rem;
  text-align: left;
}
.section-preferences-panel {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
  max-width: 100%;
}
.section-preferences-actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: fit-content;
  max-width: 100%;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  flex-wrap: wrap;
}
.section-preferences-disclosure {
  display: inline-grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.55rem;
  min-width: 15.5rem;
  min-height: 42px;
  padding: 0 0.85rem;
  border: 1px solid rgba(72, 84, 116, 0.18);
  border-radius: 9px;
  background: #f8fafc;
  color: #26364d;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;
}
.section-preferences-disclosure.active,
.section-preferences-disclosure:hover {
  border-color: rgba(72, 84, 116, 0.34);
  background: #eeeef7;
}
.section-preferences-disclosure-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}
.section-preferences-status-badge {
  display: inline-flex;
  align-items: center;
  min-height: 18px;
  padding: 0 0.45rem !important;
  border-radius: 999px;
  background: #fff0c2;
  color: #805200 !important;
  font-size: 0.68rem !important;
  font-weight: 800 !important;
  line-height: 1;
  white-space: nowrap;
}
.section-preferences-foldout {
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(72, 84, 116, 0.14);
  border-radius: 8px;
  background: #fbfcfe;
  overflow: visible;
}
.section-preferences-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.7rem 0.85rem;
  border-bottom: 1px solid rgba(72, 84, 116, 0.12);
  background: #f4f5f8;
  text-align: left;
  flex-wrap: wrap;
}
.section-preferences-header-copy {
  flex: 1 1 240px;
  min-width: 0;
}
.section-preferences-header-title,
.client-section-preferences strong {
  color: #142f35;
  font-size: 0.95rem;
}
.section-preferences-header-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
}
.section-preferences-header-description {
  display: block;
  padding-left: 0;
  margin-top: 0.2rem;
  color: #607178;
  font-size: 0.82rem;
}
.section-preferences-save {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 34px;
  padding: 0 0.9rem;
  border: 0;
  border-radius: 7px;
  background: #2364aa;
  color: #fff;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}
.section-preferences-header-actions {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1 1 280px;
}
.section-preferences-readonly-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-height: 20px;
  padding: 0.14rem 0.48rem;
  border: 1px solid rgba(96, 113, 120, 0.24);
  border-radius: 999px;
  background: #eef1f3;
  color: #607178;
  font-size: 0.64rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1;
}
.section-preferences-restore {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 34px;
  padding: 0 0.75rem;
  border: 1px solid rgba(72, 84, 116, 0.18);
  border-radius: 7px;
  background: #ffffff;
  color: #26364d;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
}
.section-preferences-restore:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}
.section-preferences-restore > span,
.section-preferences-restore > i {
  padding-left: 0;
  margin-top: 0;
}
.settings-info-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  color: #2364aa;
}
.settings-info-tooltip {
  position: absolute;
  right: 0;
  bottom: calc(100% + 8px);
  z-index: 10000;
  display: none;
  width: 230px;
  padding: 0.55rem 0.65rem;
  border-radius: 7px;
  background: #102a43;
  color: #fff;
  font-size: 0.76rem;
  font-weight: 600;
  line-height: 1.25;
  box-shadow: 0 8px 18px rgba(16, 42, 67, 0.2);
}
.mystudio-default-tooltip {
  width: 320px;
  max-width: min(320px, calc(100vw - 2rem));
  color: #fff;
  text-align: left;
}
.mystudio-default-tooltip-title,
.mystudio-default-tooltip-copy,
.mystudio-default-tooltip-list,
.mystudio-default-tooltip-list span {
  display: block;
  padding-left: 0;
  color: inherit;
}
.mystudio-default-tooltip-title {
  margin-bottom: 0.35rem;
  font-size: 0.82rem;
  font-weight: 900;
}
.mystudio-default-tooltip-copy {
  margin-top: 0.25rem;
}
.mystudio-default-tooltip-list {
  max-height: 160px;
  margin-top: 0.4rem;
  overflow-y: auto;
}
.mystudio-default-tooltip-list span {
  position: relative;
  padding-left: 0.75rem;
  margin-top: 0.18rem;
  line-height: 1.25;
}
.mystudio-default-tooltip-list span::before {
  content: "-";
  position: absolute;
  left: 0;
}
.settings-info-icon-wrapper:hover .settings-info-tooltip {
  display: block;
}
.section-preferences-save:disabled {
  cursor: not-allowed;
  background: #8fb2d7;
  opacity: 0.72;
}
.section-preferences-reset {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  min-height: 32px;
  padding: 0.35rem 0.7rem;
  border: 1px solid rgba(116, 129, 143, 0.28);
  border-radius: 8px;
  background: #ffffff;
  color: #34495e;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
}
.section-preferences-reset:hover:not(:disabled) {
  border-color: rgba(116, 129, 143, 0.42);
  background: #f6f8fb;
}
.section-preferences-reset:disabled {
  cursor: not-allowed;
  color: #8a98a8;
  background: #f7f8fa;
  opacity: 0.72;
}
.section-preferences-reset span,
.section-preferences-reset i {
  color: inherit;
  padding-left: 0;
  margin-top: 0;
}
.section-preferences-save span,
.section-preferences-save i {
  color: #fff;
  padding-left: 0;
  margin-top: 0;
}
.section-preferences-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.45rem;
  padding: 0.75rem;
}
.section-preference-row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 36px;
  padding: 0.42rem 0.58rem;
  border: 1px solid rgba(35, 100, 170, 0.14);
  border-radius: 8px;
  background: #ffffff;
  color: #142f35;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.25;
  text-align: left;
}
.section-preference-row input {
  width: 16px;
  height: 16px;
  margin: 0;
  flex: 0 0 auto;
}
.section-preference-name {
  color: #09283d;
  font-size: 0.75trem;
  font-weight: 800;
  letter-spacing: 0.012em;
  line-height: 1.22;
  text-transform: uppercase;
}
.section-preference-row.permanent-diff {
  border-color: rgba(38, 143, 106, 0.48);
  background: #f0fbf6;
  box-shadow: inset 2px 0 0 #34a66f, 0 1px 6px rgba(38, 143, 106, 0.08);
}
.section-preference-row.permanent-diff input {
  accent-color: #1f9d63;
}
.section-preference-row.permanent-diff-excluded input {
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(38, 143, 106, 0.14);
}
.section-preference-row.permanent-diff-included input {
  box-shadow: 0 0 0 2px rgba(38, 143, 106, 0.16);
}
.section-preference-row.changed {
  border-color: rgba(214, 158, 46, 0.72);
  background: #fff9e8;
  box-shadow: inset 2px 0 0 #f2b705, 0 1px 6px rgba(180, 127, 0, 0.08);
}
.section-preference-row.changed input {
  accent-color: #e8a900;
}
.section-preference-row.changed-excluded input {
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(242, 183, 5, 0.16);
}
.section-preference-row.changed-included input {
  box-shadow: 0 0 0 2px rgba(242, 183, 5, 0.18);
}
.client-section-preferences {
  padding: 0.75rem;
  text-align: left;
}
.client-section-preference-create {
  display: grid;
  gap: 0.35rem;
  margin-bottom: 0.65rem;
  padding: 0.45rem 0.5rem;
  border: 1px solid rgba(72, 84, 116, 0.1);
  border-radius: 7px;
  background: #fbfcfe;
}
.client-section-preference-create-label {
  padding-left: 0;
  color: #607178;
  font-size: 0.78rem;
  font-weight: 700;
}
.client-section-preference-create-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px;
  align-items: center;
  gap: 0.55rem;
  width: 100%;
}
.client-section-preference-create-row .software-select {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.section-preferences-create {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  min-width: 34px;
  height: 34px;
  border: 1px solid rgba(35, 100, 170, 0.28);
  border-radius: 8px;
  background: #eef5ff;
  color: #2364aa;
  font-size: 0.86rem;
  font-weight: 900;
  cursor: pointer;
}
.section-preferences-create:hover:not(:disabled) {
  border-color: rgba(35, 100, 170, 0.46);
  background: #e2efff;
}
.section-preferences-create:disabled {
  cursor: not-allowed;
  color: #8a98a8;
  background: #f1f3f5;
  opacity: 0.74;
}
.section-preferences-create i {
  color: inherit;
  padding-left: 0;
  margin-top: 0;
}
.client-section-preferences-list {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.55rem;
}
.client-section-preference-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.48rem 0.65rem;
  border-radius: 7px;
  background: #ffffff;
  border: 1px solid rgba(30, 94, 80, 0.1);
  color: #21463e;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: left;
}
.client-section-preference-row.active,
.client-section-preference-row:hover {
  border-color: rgba(35, 100, 170, 0.32);
  background: #f4f7fc;
}
.client-section-preference-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
  flex: 1 1 auto;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.client-section-preference-row small {
  color: #607178;
  font-size: 0.72rem;
  white-space: nowrap;
}
.client-section-preference-delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  border: 1px solid rgba(96, 113, 120, 0.22);
  border-radius: 7px;
  background: #f7f8fa;
  color: #607178;
  cursor: pointer;
}
.client-section-preference-delete:hover {
  border-color: rgba(96, 113, 120, 0.38);
  background: #eef1f4;
  color: #394950;
}
.client-section-preference-editor {
  margin-top: 0.75rem;
  overflow: hidden;
  border: 1px solid rgba(72, 84, 116, 0.12);
  border-radius: 8px;
  background: #ffffff;
}
.client-section-editor-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 1rem;
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid rgba(72, 84, 116, 0.12);
  background: #f4f5f8;
  text-align: left;
}
.client-section-editor-copy {
  display: flex;
  flex-direction: column;
  gap: 0.18rem;
  min-width: 0;
}
.client-section-editor-copy strong {
  display: block;
  color: #142f35;
  font-size: 0.95rem;
}
.client-section-editor-copy span {
  padding-left: 0;
  color: #607178;
  font-size: 0.82rem;
  line-height: 1.35;
}
.client-section-editor-copy .client-section-editor-name {
  color: #142f35;
  font-size: 0.88rem;
  font-weight: 800;
}
.client-section-editor-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.label-with-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

/* Icona info */
.info-icon-wrapper {
  position: relative;
  display: inline-block;
}
.info-icon-wrapper:hover .tooltip-software {
  visibility: visible;
  opacity: 1;
  top: 32px;
}
.info-icon-software {
  color: #4a90e2;
  font-size: 0.95rem;
  cursor: default;
  transition: color 0.2s;
}

.info-icon-software:hover {
  color: #2b6cb0;
}

/* Tooltip */
.tooltip-software {
  visibility: hidden;
  opacity: 0;
  position: absolute;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  white-space: nowrap;
  z-index: 100;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  width: max-content;
  text-align: center;
}

/* Freccetta del tooltip */
.tooltip-software::before {
  content: "";
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #333;
}
.functions {
  margin-bottom: 2rem;
}

/* === NEW SETTINGS DASHBOARD LAYOUT === */
.settings-page {
  --settings-page-gutter: clamp(1rem, 5vw, 6rem);
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  max-width: none;
  height: 100%;
  min-height: 0;
  margin: 0;
  padding: clamp(0.55rem, 1.25dvh, 0.85rem) var(--settings-page-gutter)
    clamp(0.45rem, 1dvh, 0.7rem);
  color: #082840;
  text-align: left;
  overflow: hidden;
}

.settings-grid {
  display: grid;
  flex: 1 1 0;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-template-rows:
    minmax(10.4rem, 1fr)
    minmax(15.9rem, 1.55fr)
    minmax(8.9rem, 0.86fr);
  min-height: 0;
  margin-top: 0.75rem;
  gap: clamp(0.78rem, 1.45vh, 1.18rem) clamp(0.9rem, 1.2vw, 1.28rem);
  align-items: stretch;
  align-content: stretch;
  padding: 0.12rem 0.22rem 0.35rem;
  overflow: hidden;
}

.settings-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  border: 1px solid rgba(30, 112, 89, 0.08);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 7px 22px rgba(8, 40, 64, 0.09);
  text-align: left;
}

.settings-card--wide {
  grid-column: span 3;
  padding: clamp(0.82rem, 1.25vh, 1.08rem) 1.48rem clamp(0.8rem, 1.2vh, 1.05rem);
}

.settings-card--compact {
  grid-column: span 2;
  padding: clamp(0.8rem, 1.2vh, 1rem) 1.42rem clamp(0.82rem, 1.2vh, 1.05rem);
}

.settings-card--full {
  grid-column: 1 / -1;
  padding: clamp(0.76rem, 1.1vh, 0.95rem) 1.42rem
    clamp(0.78rem, 1.15vh, 1.02rem);
}

.settings-card-header {
  display: grid;
  grid-template-columns: 2.85rem minmax(0, 1fr);
  align-items: center;
  gap: 1.05rem;
}

.settings-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.85rem;
  height: 2.85rem;
  border-radius: 9px;
  background: linear-gradient(135deg, #e6f8ec 0%, #dcf4e5 100%);
  color: #14765e;
  font-size: 1.45rem;
  box-shadow: inset 0 0 0 1px rgba(20, 118, 94, 0.03);
}

.settings-icon-box--appearance {
  background: linear-gradient(135deg, #f2e8ff 0%, #e7d7ff 100%);
  color: #7c3aed;
  box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.06);
}

.settings-icon-box--software {
  background: linear-gradient(135deg, #e6f5ff 0%, #d7edff 100%);
  color: #0b74bb;
  box-shadow: inset 0 0 0 1px rgba(11, 116, 187, 0.06);
}

.settings-icon-box--paghe {
  background: rgba(22, 136, 95, 0.13);
  color: #076247;
  box-shadow: none;
}

.settings-icon-box--f24 {
  background: rgba(22, 139, 210, 0.12);
  color: #075994;
  box-shadow: none;
}

.settings-icon-box--bilancio {
  background: rgba(183, 121, 9, 0.12);
  color: #795006;
  box-shadow: none;
}

.settings-icon-box .product-icon {
  width: 84%;
  height: 84%;
  transform: scale(1.12);
}

.settings-f24-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.55rem;
  height: 1.9rem;
  border: 2px solid currentColor;
  border-radius: 2px;
  font-size: 0.48rem;
  font-style: normal;
  font-weight: 900;
  line-height: 1;
}

.settings-card-heading h4 {
  margin: 0 0 0.2rem;
  color: #0b2343;
  font-size: 1.04rem;
  font-weight: 900;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.settings-card-heading p,
.settings-help-text,
.settings-row-copy small,
.settings-row-note {
  margin: 0;
  color: #66758d;
  font-size: 0.77rem;
  font-weight: 500;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.settings-card-divider {
  height: 1px;
  margin: 0.95rem 0 0.85rem;
  background: rgba(25, 72, 105, 0.16);
}

.settings-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
}

.settings-row--inline {
  min-height: 2.3rem;
}

.settings-row--tight {
  align-items: flex-start;
  min-height: 2.25rem;
  margin-bottom: 0.7rem;
}

.settings-row--tight > .settings-row-label,
.settings-row--tight > .settings-label-info {
  flex: 1 1 auto;
  min-width: 0;
}

.settings-row--full {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  column-gap: 0.75rem;
}

.settings-row-main {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.7rem;
  min-width: 0;
  padding-right: 0.75rem;
}

.settings-row-copy {
  display: grid;
  gap: 0.42rem;
  min-width: 0;
}

.settings-row-label {
  color: #0b2b48;
  font-size: 0.84rem;
  font-weight: 750;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.settings-row-note {
  min-height: 1.9rem;
  padding-left: 0.85rem;
  border-left: 1px solid rgba(25, 72, 105, 0.2);
  display: inline-flex;
  align-items: center;
}

.settings-label-info {
  display: inline-flex;
  align-items: center;
  gap: 0.42rem;
  flex: 1 1 auto;
  min-width: 0;
}

.settings-label-info .settings-row-label {
  min-width: 0;
}

.settings-field-block {
  display: grid;
  gap: 0.45rem;
  margin-top: 0.42rem;
}

.settings-select {
  width: 14rem;
  height: 2.08rem;
  min-width: 14rem;
  padding: 0 2.1rem 0 0.8rem;
  border: 1px solid rgba(44, 62, 80, 0.22);
  border-radius: 7px;
  background: #fff;
  color: #111827;
  font-size: 0.82rem;
  font-weight: 600;
}

.settings-select--full {
  width: 100%;
  min-width: 0;
}

.settings-help-text {
  margin-top: 0.72rem;
}

.settings-row.settings-row--f24-aggregate {
  margin-bottom: 0.08rem;
}

.settings-help-text.settings-help-text--f24-aggregate {
  margin-top: 0;
}

.settings-card--full .settings-card-divider {
  margin: 0.78rem 0 0.72rem;
}

.settings-card--full .settings-row--full {
  flex: 1 1 auto;
  min-height: 2.5rem;
}

.section-preferences-open-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  min-height: 2.08rem;
  padding: 0 0.85rem;
  border: 1px solid rgba(33, 105, 81, 0.24);
  border-radius: 8px;
  background: #f4fbf7;
  color: #216951;
  font-size: 0.8rem;
  font-weight: 850;
  cursor: pointer;
}

.section-preferences-open-button:hover:not(:disabled) {
  border-color: rgba(33, 105, 81, 0.42);
  background: #e9f7ef;
}

.section-preferences-open-button:disabled {
  cursor: wait;
  opacity: 0.7;
}

.section-preferences-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(4, 22, 36, 0.42);
  backdrop-filter: blur(3px);
}

.section-preferences-modal {
  width: min(72rem, calc(100vw - 4rem));
  min-height: 32rem;
  max-height: min(84vh, 50rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(33, 105, 81, 0.16);
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(8, 40, 64, 0.22);
}

.section-preferences-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.05rem 1.2rem;
  border-bottom: 1px solid rgba(25, 72, 105, 0.14);
  background: linear-gradient(180deg, #f8fbfa 0%, #eef6f2 100%);
}

.section-preferences-modal-eyebrow {
  display: block;
  margin-bottom: 0.2rem;
  color: #216951;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.section-preferences-modal-header h4 {
  margin: 0;
  color: #0b2343;
  font-size: 1.08rem;
  font-weight: 900;
}

.section-preferences-modal-header p {
  margin: 0.18rem 0 0;
  color: #66758d;
  font-size: 0.82rem;
}

.section-preferences-modal-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid rgba(96, 113, 120, 0.2);
  border-radius: 999px;
  background: #ffffff;
  color: #52636b;
  cursor: pointer;
}

.section-preferences-modal-close:hover {
  color: #0b2343;
  border-color: rgba(96, 113, 120, 0.36);
  background: #f7f9fb;
}

.section-preferences-modal > .section-preferences-empty,
.section-preferences-modal > .section-preferences-panel {
  margin: 1rem;
  width: auto;
  max-width: calc(100% - 2rem);
  box-sizing: border-box;
  align-self: stretch;
}

.section-preferences-modal > .section-preferences-panel {
  min-height: 0;
  min-width: 0;
  overflow: auto;
}

.section-preferences-modal .section-preferences-actions {
  width: 100%;
}

.section-preferences-modal .section-preferences-foldout {
  overflow: hidden;
}

.section-preferences-modal .section-preferences-list {
  grid-template-columns: repeat(auto-fit, minmax(20rem, 1fr));
}

.section-preferences-modal .section-preferences-empty {
  width: auto;
  margin-left: 0;
}

.toggle-switch {
  width: 2.8rem;
  height: 1.55rem;
  flex: 0 0 2.8rem;
}

.slider {
  background-color: #c8c9cb;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.12);
}

.slider:before {
  width: 1.15rem;
  height: 1.15rem;
  left: 0.18rem;
  bottom: 0.2rem;
}

input:checked + .slider {
  background-color: #37b54a;
}

input:checked + .slider:before {
  transform: translateX(1.25rem);
}

.settings-theme-switch {
  width: 3.32rem;
  height: 1.55rem;
  flex: 0 0 3.32rem;
}

.slider-dark {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #c8c9cb;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15);
  transition: 0.25s ease;
}

.slider-dark::before {
  content: "";
  position: absolute;
  left: 0.18rem;
  bottom: 0.2rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  background: #fff;
  transition: 0.25s ease;
}

.theme-switch input:checked + .slider-dark {
  background: #37b54a;
}

.theme-switch input:checked + .slider-dark::before {
  transform: translateX(1.78rem);
}

.settings-theme-switch .moon-icon {
  position: absolute;
  right: 0.42rem;
  top: 50%;
  z-index: 1;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.68rem;
  transform: translateY(-50%);
  pointer-events: none;
}

.section-preferences-compact-area {
  margin-top: 0.75rem;
}

.settings-card .section-preferences-empty {
  width: 100%;
  margin: 0.55rem 0 0;
  padding: 0.65rem 0.75rem;
  font-size: 0.76rem;
}

.settings-card .section-preferences-panel {
  gap: 0.55rem;
}

.settings-card .section-preferences-actions {
  width: 100%;
}

.settings-card .section-preferences-disclosure {
  min-width: 0;
  min-height: 2rem;
  flex: 1 1 12rem;
  padding: 0 0.62rem;
  font-size: 0.74rem;
}

.settings-card .section-preferences-foldout {
  max-height: 21rem;
  overflow: auto;
}

.settings-card .section-preferences-header {
  padding: 0.58rem 0.65rem;
}

.settings-card .section-preferences-header-actions {
  gap: 0.35rem;
}

.settings-card .section-preferences-list {
  grid-template-columns: 1fr;
  gap: 0.35rem;
  padding: 0.55rem;
}

.settings-card .section-preference-row {
  min-height: 2rem;
  padding: 0.34rem 0.48rem;
  font-size: 0.74rem;
}

.settings-card .client-section-editor-header {
  grid-template-columns: 1fr;
}

.info-icon-software {
  color: #2e82b8;
  font-size: 0.78rem;
}

.tooltip-software {
  white-space: normal;
  width: 18rem;
  max-width: min(18rem, calc(100vw - 2rem));
  text-align: left;
}

.dm .settings-card {
  border-color: rgba(255, 255, 255, 0.08);
  background: #253344;
}

.dm .settings-title,
.dm .settings-title::after {
  color: #9de3c6;
}

.dm .settings-title::after {
  background: #9de3c6;
}

.dm .settings-card-heading h4,
.dm .settings-row-label {
  color: #edf6f9;
}

.dm .settings-card-heading p,
.dm .settings-help-text,
.dm .settings-row-copy small,
.dm .settings-row-note {
  color: #b9c6d2;
}

.dm .settings-card-divider {
  background: rgba(255, 255, 255, 0.14);
}

.dm .settings-select {
  border-color: rgba(255, 255, 255, 0.18);
  background: #1d2936;
  color: #edf6f9;
}

@media (max-width: 1180px) {
  .settings-page {
    width: 100%;
  }

  .settings-card--wide {
    grid-column: span 3;
  }
}

@media (max-width: 980px) {
  .settings-page {
    --settings-page-gutter: 1rem;
  }
}

@media (min-width: 861px) and (max-height: 900px) {
  .settings-page {
    padding: 0.38rem var(--settings-page-gutter) 0.28rem;
  }

  .settings-grid {
    grid-template-rows: auto auto auto;
    align-content: start;
    margin-top: 0.42rem;
    gap: 0.55rem 0.9rem;
    padding: 0.08rem 0.18rem 0.18rem;
  }

  .settings-card--wide,
  .settings-card--compact,
  .settings-card--full {
    padding-top: 0.62rem;
    padding-bottom: 0.62rem;
  }

  .settings-card--full {
    min-height: 7.4rem;
    padding-bottom: 0.9rem;
  }

  .settings-row--full {
    grid-template-columns: max-content minmax(0, 1fr);
    align-items: center;
  }

  .settings-card-divider {
    margin: 0.55rem 0 0.48rem;
  }

  .settings-card--full .settings-card-divider {
    margin: 0.48rem 0 0.42rem;
  }

  .settings-row--inline {
    min-height: 2rem;
  }

  .settings-row--tight {
    min-height: 1.95rem;
    margin-bottom: 0.22rem;
  }

  .settings-row-copy {
    gap: 0.24rem;
  }

  .settings-field-block {
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.28rem 0.45rem;
    margin-top: 0;
  }

  .settings-field-block > .settings-label-info {
    grid-column: 1 / -1;
  }

  .settings-field-block .section-preferences-open-button {
    width: auto;
    min-width: 11.5rem;
    white-space: nowrap;
  }

  .settings-select,
  .section-preferences-open-button {
    min-height: 1.92rem;
    height: 1.92rem;
  }

  .settings-help-text {
    margin-top: 0.4rem;
  }

  .settings-field-block + .settings-help-text {
    display: none;
  }
}

@media (max-width: 860px) {
  .settings-page {
    width: 100%;
    height: auto;
    max-height: none;
    min-height: auto;
    padding-top: 1rem;
    overflow: visible;
  }

  .settings-grid {
    flex: none;
    grid-template-columns: 1fr;
    grid-template-rows: none;
    grid-auto-rows: auto;
    overflow: visible;
  }

  .settings-card--wide,
  .settings-card--compact,
  .settings-card--full {
    grid-column: 1;
    min-height: 0;
  }

  .settings-row--full {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 0.6rem;
  }

  .settings-row--full::before {
    display: none;
  }

  .settings-row-main {
    padding-right: 0;
  }

  .settings-row-note {
    grid-column: 1 / -1;
    padding-left: 0;
    border-left: 0;
  }
}
</style>
